import React from 'react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { AlertCircle, Save } from 'react-feather';
import { useSearchParams } from 'react-router-dom';
import ClientPageCardHeader from 'ts/common/components/ClientPageCardHeader';
import QuestionnaireCompletedView from 'ts/common/components/questionnaire/QuestionnaireCompletedView';
import QuestionnairePageForm from 'ts/common/components/questionnaire/QuestionnairePageForm';
import { useTranslate } from 'ts/common/hooks';
import { formatDate } from 'ts/common/questionnaire/common';

export interface QuestionnaireError {
    detail?: string;
    status: number;
}

export const isQuestionnaireError = (response: unknown): response is QuestionnaireError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as QuestionnaireError).status === 'number' &&
        (response as QuestionnaireError).status >= 400
    );
};

interface Props {
    questionnaire: SpApi.Client.IQuestionnaire;
    saveQuestionResponse: (questionId: number, responseText: string | null) => Promise<void>;
    submitQuestionnaire: () => void;
    saving: boolean;
    submitting: boolean;
    error: QuestionnaireError | null;
    savingFailed: boolean;
}

const defaultPageIndex = 0;

const Questionnaire: React.FC<Props> = ({
    questionnaire,
    saveQuestionResponse,
    submitQuestionnaire,
    saving,
    submitting,
    error,
    savingFailed
}) => {
    const t = useTranslate('clientQuestionnaire');
    const isLargeScreen = useBreakpointValue({ base: false, lg: true });
    const [searchParams, setSearchParams] = useSearchParams();

    const isCompleted = questionnaire.questionnaireStatus === 'completed';
    const sortedPages = [...questionnaire.pages].sort((a, b) => a.displayOrder - b.displayOrder);

    let pageIndex = searchParams.has('page') ? Number(searchParams.get('page')) - 1 : undefined;

    if (pageIndex === undefined) {
        // If no page index is provided, then the default page index is the last page with responses, or else the first page:

        const reversedLastPageIndexWithResponses = [...sortedPages]
            .reverse()
            .findIndex((page) => page.questions.some((question) => question.responseText !== null));

        pageIndex =
            reversedLastPageIndexWithResponses === -1
                ? defaultPageIndex
                : sortedPages.length - 1 - reversedLastPageIndexWithResponses;
    }

    if (!sortedPages[pageIndex]) {
        pageIndex = defaultPageIndex;
    }

    return (
        <Flex direction="column" gap="24px" marginBottom="24px">
            <ClientPageCardHeader
                headingText={questionnaire.name}
                statusText={t(`statuses.${questionnaire.questionnaireStatus}`)}
            />
            <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-start">
                <Flex flexDirection="column" gap="8px">
                    <Flex
                        as="dl"
                        margin={0}
                        fontSize="14px"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={{ base: '40px', md: '80px' }}
                        alignItems="flex-start"
                    >
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('fromLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.brandName}</Text>
                        </Flex>
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('toLabel')}
                            </Text>
                            <Text as="dd">{questionnaire.contact.name}</Text>
                        </Flex>
                        <Flex flexDirection="column" gap="4px">
                            <Text as="dt" fontWeight="600" marginBottom={0}>
                                {t('dueDateLabel')}
                            </Text>
                            <Text as="dd">
                                {questionnaire.dueDate
                                    ? formatDate(questionnaire.dueDate, 'UTC')
                                    : '-'}
                            </Text>
                        </Flex>
                        {questionnaire.questionnaireStatus === 'completed' &&
                            !!questionnaire.completed && (
                                <Flex flexDirection="column" gap="4px">
                                    <Text as="dt" fontWeight="600" marginBottom={0}>
                                        {t('submittedOnLabel')}
                                    </Text>
                                    <Text as="dd">{formatDate(questionnaire.completed)}</Text>
                                </Flex>
                            )}
                    </Flex>
                    {questionnaire.descriptionHtml && (
                        <Box
                            fontFamily="open-sans"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(questionnaire.descriptionHtml)
                            }}
                        />
                    )}
                </Flex>
                {!isCompleted &&
                    isLargeScreen &&
                    (saving || questionnaire.questionnaireStatus === 'partial') && (
                        <Flex
                            flexDirection="row"
                            flexWrap="nowrap"
                            alignItems="center"
                            gap="8px"
                            width="120px"
                            color={savingFailed ? 'red' : 'inherit'}
                        >
                            {saving || !savingFailed ? <Save /> : <AlertCircle />}
                            <Text color="inherit">
                                {saving ? t('saving') : savingFailed ? t('error') : t('draftSaved')}
                            </Text>
                        </Flex>
                    )}
            </Flex>
            {isCompleted ? (
                <QuestionnaireCompletedView sortedPages={sortedPages} />
            ) : (
                sortedPages[pageIndex] && (
                    <QuestionnairePageForm
                        questionnairePage={sortedPages[pageIndex]}
                        pageIndex={pageIndex}
                        totalPages={questionnaire.pages.length}
                        setSearchParams={setSearchParams}
                        saveQuestionResponse={saveQuestionResponse}
                        onSubmit={submitQuestionnaire}
                        submitting={submitting}
                        savingFailed={savingFailed}
                        error={error}
                    />
                )
            )}
        </Flex>
    );
};

export default Questionnaire;
